<template>
  <ValidationObserver ref="observer" rules="required" v-slot="{ handleSubmit }">
    <RightSideBar
      v-if="isOpen"
      @close="onClose"
      :loading="isLoading"
      @submit="handleSubmit(onSubmit)"
      submit="Save"
      :close-button="isViewMode"
      button-class="bg-dynamicBackBtn text-white rounded"
      :disabled-button="isLoading"
      width="width:520px"
    >
      <template v-slot:title>
        <h1 class="text-darkPurple font-bold text-lg">
          {{ isViewMode ? "View Holiday" : holiday.holidayName ? "Edit Holiday" : "Create new holiday" }}
        </h1>
      </template>
      <template v-slot:default>
        <div class="flex flex-col justify-start gap-8">
          <div class="flex flex-col gap-5">
              <c-text
                :key="`holidayName-${holidayId || 'new'}`"
                label="Holiday Name"
                placeholder="--Enter--"
                variant="w-full h-10"
                :rules="['required']"
                v-model="holiday.holidayName"
                :disabled="isViewMode"
              />

              <c-select
                label="Holiday Category"
                placeholder="--e.g --"
                :options="categoryOptions"
                v-model="holiday.holidayCategory"
                :disabled="isViewMode"
                :rules="['required']"
                option-value="id"
                option-label="name"
              />

              <validation-provider class="w-full" name="Date" v-slot="{ errors }">
              <label class="date-label">Date</label>
              <div class="border border-romanSilver rounded-md pl-2 w-full">
                <div class="flex justify-start items-center gap-2 w-full h-10">
                  <div class="flex justify-center border-r border-romanSilver h-6 pr-2">
                    <Icon icon-name="calendar" size="xs" />
                  </div>
                  <div class="h-10 mt-1 w-full">
                    <datepicker
                      placeholder="--Select--"
                      input-class="date-input"
                      style="width: 100%; outline: none"
                      v-model="holiday.date"
                      :disabled="isViewMode"
                    />
                  </div>
                </div>
              </div>
              <small v-if="errors.length" style="font-size: 12px; color: red;">Date is not valid</small>
            </validation-provider>

            <div>
              <div class="flex justify-between items-center mb-2">
                <label class="label">Location</label>
                <div v-if="!isViewMode" class="flex items-center gap-1">
                  <c-checkbox
                    v-model="selectAllLocations"
                  />
                  <span class="text-sm text-jet cursor-pointer" @click="selectAllLocations = !selectAllLocations">
                    Select all locations
                  </span>
                </div>
              </div>
              <v-select
                :clearable="false"
                class="style-select"
                multiple
                :disabled="isViewMode"
                :reduce="location => location.id"
                :options="locationOptions"
                :close-on-select="false"
                v-model="holiday.locationIds"
                placeholder="--eg--"
              >
                <template #option="{ name, id }">
                  <div class="flex">
                    <div class="hover:text-white">
                      <div class="flex justify-start items-center gap-2">
                        <c-checkbox :true-value="!holiday.locationIds.includes(id)" @focus.prevent :key="id" />
                        <span class="font-bold text-base cursor-pointer">{{ name }}</span>
                      </div>
                    </div>
                  </div>
                </template>
                <template #selected-option="{name}">
                  {{ name }}
                </template>
                <template #open-indicator="{ attributes }">
                  <span v-bind="attributes">
                    <Icon icon-name="caret-down" size="xsm" />
                  </span>
                </template>
              </v-select>
            </div>

            <c-select
              label="Recurring"
              placeholder="--e.g --"
              :options="recurringOptions"
              v-model="holiday.recurring"
              :disabled="isViewMode"
              :rules="['required']"
              option-value="id"
              option-label="name"
            />

            <div>
              <label class="label">Description</label>
              <c-textarea
                placeholder="Text Area"
                variant="w-full"
                :maxlength="300"
                v-model="holiday.description"
                :disabled="isViewMode"
              />
            </div>

            <div class="flex flex-col justify-start gap-3">
              <div class="flex flex-col justify-start gap-0">
                <h3 class="text-base font-semibold text-darkPurple">
                  Shift the holiday to the next working day if it falls on a weekend.
                </h3>
              </div>
              <c-radio
                class="c-radio"
                space-between="mr-2 ml-2"
                :rules="['required']"
                :disabled="isViewMode"
                :value="holiday.skipWeekend"
                @input="updateSkipWeekend"
                :options="[
                  { name: 'Yes', value: true },
                  { name: 'No', value: false },
                ]"
              />
            </div>
          </div>
        </div>
      </template>
    </RightSideBar>
  </ValidationObserver>
</template>

<script>
import { ValidationObserver, ValidationProvider } from "vee-validate";
import CSelect from "@scelloo/cloudenly-ui/src/components/select";
import CRadio from "@scelloo/cloudenly-ui/src/components/radio";
import CCheckbox from "@scelloo/cloudenly-ui/src/components/checkbox";
import CText from "@scelloo/cloudenly-ui/src/components/text";
import CTextarea from "@scelloo/cloudenly-ui/src/components/text-area";
import RightSideBar from "@/components/RightSideBar";
import Icon from "@/components/Icon";
import Datepicker from "vuejs-datepicker";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";

export default {
  components: {
    ValidationObserver,
    ValidationProvider,
    CSelect,
    CRadio,
    CText,
    CTextarea,
    CCheckbox,
    RightSideBar,
    Icon,
    Datepicker,
    "v-select": vSelect,
  },
  data() {
    return {
      isOpen: false,
      isLoading: false,
      isViewMode: false,
      categoryOptions: [
        { name: "National holiday", id: "National holiday" },
        { name: "State holiday", id: "State holiday" },
        { name: "Religious holiday", id: "Religious holiday" },
        { name: "Company holiday", id: "Company holiday" },
      ],
      locationOptions: [],
      recurringOptions: [
        { name: "Yes", id: true },
        { name: "No", id: false },
      ],
      holidayId: null,
      holiday: {
        holidayName: "",
        holidayCategory: null,
        date: null,
        locationIds: [],
        recurring: null,
        description: "",
        skipWeekend: null,
      },
    };
  },
  computed: {
    descriptionLength() {
      return this.holiday.description ? this.holiday.description.length : 0;
    },
    selectAllLocations: {
      get() {
        return (
          this.locationOptions.length > 0 &&
          this.locationOptions.every((location) =>
            this.holiday.locationIds.includes(location.id)
          )
        );
      },
      set(value) {
        if (value) {
          // Select all locations
          this.holiday.locationIds = this.locationOptions.map((location) => location.id);
        } else {
          // Deselect all locations
          this.holiday.locationIds = [];
        }
      },
    },
  },
  methods: {
    updateSkipWeekend(value) {
      this.holiday.skipWeekend = this.normalizeBoolean(value);
    },
    normalizeBoolean(value) {
      if (value === null || value === undefined) return null;
      if (typeof value === 'boolean') return value;
      if (typeof value === 'string') {
        if (value.toLowerCase() === 'true') return true;
        if (value.toLowerCase() === 'false') return false;
      }
      return Boolean(value);
    },
    async onOpen(holiday = {}, actionType) {
      try {
        this.isLoading = true;
        this.isOpen = true;
        this.holidayId = null;

        if (actionType === "view") this.isViewMode = true;
        if (actionType === "edit" || actionType === "create") this.isViewMode = false;

        // Ensure locations are loaded
        if (this.locationOptions.length === 0) {
          await this.fetchLocations();
        }

        if (holiday && Object.keys(holiday).length) {
          this.holidayId = holiday.id;

          this.holiday = {
            holidayName: holiday.holidayName || "",
            holidayCategory: holiday.holidayCategory || null,
            date: holiday.date ? new Date(holiday.date) : null,
            locationIds: holiday.locationIds || [],
            recurring: this.normalizeBoolean(holiday.recurring),
            description: holiday.description || "",
            skipWeekend: this.normalizeBoolean(holiday.skipWeekend),
          };
        } else {
          this.holiday = {
            holidayName: "",
            holidayCategory: null,
            date: null,
            locationIds: [],
            recurring: null,
            description: "",
            skipWeekend: null,
          };
        }

        this.isLoading = false;
      } catch (error) {
        this.$toasted.error(error?.message || "Failed to load holiday data", { duration: 3000 });
        this.isLoading = false;
      }
    },
    async onSubmit() {
      this.isLoading = true;
      const orgId = this.$AuthUser?.orgId;

      try {
        const payload = {
          orgId,
          holidayName: this.holiday.holidayName,
          holidayCategory: this.holiday.holidayCategory,
          fromDate: this.holiday.date,
          toDate: this.holiday.date,
          locationIds: this.holiday.locationIds,
          recurring: this.normalizeBoolean(this.holiday.recurring),
          description: this.holiday.description,
          skipWeekend: this.normalizeBoolean(this.holiday.skipWeekend) || false,
        };

        if (this.holidayId) {
          await this.updatePublicHoliday(this.holidayId, payload);
        } else {
          const { data } = await this.$_createPublicHoliday(payload);

          const message = data?.message || "Holiday saved successfully";
          this.$toasted.success(message, { duration: 3000 });
          this.$emit("success", data);
          this.onClose();
        }
      } catch (error) {
        const errorMessage = error?.response?.data?.message || error?.message || "Failed to save holiday. Please try again.";
        this.$toasted.error(errorMessage, { duration: 3000 });
        this.$emit("error", error);
      } finally {
        this.isLoading = false;
      }
    },
    async updatePublicHoliday(holidayId, payload) {
      try {
        const { data } = await this.$_updatePublicHoliday({
          requestId: holidayId,
          payload,
        });

        const message = data?.message || "Holiday updated successfully";
        this.$toasted.success(message, { duration: 3000 });
        this.$emit("success", data);
        this.onClose();
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to update holiday. Please try again.";
        this.$toasted.error(errorMessage, { duration: 3000 });
        this.$emit("error", error);
        throw error; // Re-throw to be caught by onSubmit's catch block
      }
    },
    onClose() {
      this.isLoading = false;
      this.isOpen = false;
      this.isViewMode = false;
      this.holidayId = null;
      Object.assign(this.$data.holiday, this.$options.data().holiday);
      this.$emit("close");
    },
    async fetchLocations() {
      this.isLoading = true;
      try {
        const { data } = await this.$_getHumanarLocations();
        this.locationOptions = data.outlets.map((loc) => ({
          name: loc.name,
          id: loc.id,
        }));
      } catch (error) {
        const errorMessage = error?.response?.data?.message || error?.message || "Failed to fetch locations.";
        this.$toasted.error(errorMessage, { duration: 3000 });
      } finally {
        this.isLoading = false;
      }
    },
  },
  async mounted() {
    await this.fetchLocations();
  },
};
</script>

<style scoped>
.c-radio {
  width: 100% !important;
  display: flex !important;
  justify-content: flex-start !important;
  gap: 20px !important;
  font-size: 16px !important;
  color: #333333 !important;
}

.label {
  display: block;
  font-weight: 400;
  font-size: 14px;
  color: #374151;
  margin-bottom: 8px;
}
</style>

